import classNames from 'classnames';
import { useAtom } from 'jotai';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';
import { ScreenTitle } from '../../components/layout/ScreenTitle';
import { EVENT_STATS_CONFIG } from '../constants/actuality.event.config';
import useActualityAggregationFieldDescriptors from '../hooks/useActualityAggregationFieldDescriptors';
import useActualityAggregationMetricDescriptors from '../hooks/useActualityAggregationMetricDescriptors';
import {
  actualityAggregatedStatsGroupBy,
  actualityAggregatedStatsGroupBySecondary,
  actualityAggregatedStatsGroupIdsSorted,
  actualityAggregatedStatsSelectedAggregatedMetric,
  actualityAggregatedStatsSelectedGroups,
} from '../store/actuality.kpi.state';
import { actualityAggregatedStatsDataPageSelected } from '../store/actuality.kpi.ui.state';
import { SWARM_PAGE_SIZE } from '../store/feed.default';

const ActualityAggregatedStatsHeader: React.FC = () => {
  const { t } = useTranslation('feed');
  const groupIdsLoadable = useRecoilValueLoadable(
    actualityAggregatedStatsGroupIdsSorted,
  );
  const selectedMetric = useRecoilValue(
    actualityAggregatedStatsSelectedAggregatedMetric,
  );

  const [selectedPage, setSelectedPage] = useAtom(
    actualityAggregatedStatsDataPageSelected,
  );

  const groupBy = useRecoilValue(actualityAggregatedStatsGroupBy);
  const subGroupBy = useRecoilValue(actualityAggregatedStatsGroupBySecondary);
  const selectedGroups = useRecoilValue(actualityAggregatedStatsSelectedGroups);

  const aggFieldsDescriptors = useActualityAggregationFieldDescriptors();
  const metricDescriptors = useActualityAggregationMetricDescriptors();

  // Function to get title for any field (from metric descriptors or event config)
  const getFieldTitle = (fieldKey: string): string => {
    // First try metric descriptors (for aggregated stats)
    const metricDescriptor =
      metricDescriptors[fieldKey as keyof typeof metricDescriptors];
    if (metricDescriptor?.title) {
      return metricDescriptor.title;
    }

    // Then try event stats config (for event fields like eventHour)
    const eventStat = EVENT_STATS_CONFIG.find(stat => stat.key === fieldKey);
    if (eventStat) {
      const eventTitle = eventStat.title(t);
      if (eventTitle) {
        return eventTitle;
      }
    }

    // Try formatting the field name
    if (fieldKey) {
      const formattedName = fieldKey
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase());
      if (formattedName && formattedName !== fieldKey) {
        return formattedName;
      }
    }

    // Final fallback
    return t`Unknown`;
  };

  const resourcePaginationLabel = (currentPage: number): React.ReactNode => {
    const startItem = (currentPage - 1) * SWARM_PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * SWARM_PAGE_SIZE, totalGroupCount);

    if (currentPage === -1) {
      return (
        <div
          data-component="resourcePaginationLabel"
          className={classNames('ltr:pr-2 rtl:pl-2')}
        >
          <div className={classNames('text-base')}>ALL</div>
        </div>
      );
    }
    return (
      <div
        data-component="resourcePaginationLabel"
        className={classNames('ltr:pr-2 rtl:pl-2')}
      >
        <div
          className={classNames('text-base')}
        >{`${startItem}—${endItem}`}</div>

        <div className={classNames('opacity-75', 'text-xs')}>
          <span
            className={classNames('text-xxs uppercase ltr:mr-1 rtl:ml-1')}
          >{`of Total:`}</span>
          {`(${totalGroupCount})`}
        </div>
      </div>
    );
  };

  const showAll = _.isEmpty(selectedGroups);
  const totalGroupCount =
    groupIdsLoadable.state === 'hasValue'
      ? _.size(groupIdsLoadable.contents)
      : 0;
  const pageCount = Math.ceil(totalGroupCount / SWARM_PAGE_SIZE);

  const metricTitle = getFieldTitle(selectedMetric);
  const groupTitle = getFieldTitle(groupBy);
  const subGroupTitle = getFieldTitle(subGroupBy);

  const title = `${metricTitle} by ${groupTitle} / ${subGroupTitle}`;
  return (
    <ScreenTitle
      title={title}
      subtitle={t`Live feed`}
      isSticky
      truncate
    ></ScreenTitle>
  );
};

export default ActualityAggregatedStatsHeader;
